// @ts-check
import React, { useEffect, useState } from "react";

import { SubmitState } from "consts";
import { actions } from "actions";
import { connect } from "unistore/preact";
import { findOptionForAge, getInitialAgeFromOptions } from "utils/age";

/**
 * @template T
 * @typedef {import('preact').ComponentType<T>} ComponentType
 */

/**
 * @typedef {import('preact').JSX.Element} JSXElement
 * @typedef {import('types/api-responses').QuestionOption} QuestionOption
 */

// First digit can never be 0 — the youngest valid age is 13.
const VALID_FIRST_DIGITS = new Set(["1", "2", "3", "4", "5", "6", "7", "8", "9"]);

/**
 * Second-digit options that keep the entered age within [MIN_AGE, MAX_AGE].
 * "1" -> 13-19, "9" -> 90, everything else -> the full decade.
 *
 * @param {string} first
 * @returns {Set<string>}
 */
const getValidSecondDigits = (first) => {
  if (first === "1") return new Set(["3", "4", "5", "6", "7", "8", "9"]);
  if (first === "9") return new Set(["0"]);
  return new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"]);
};

/** @type {(string | null)[]} */
const NUMPAD_KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", null, "0", "backspace"];

/**
 * @typedef {Object} AgeNumpadQuestionProps
 * @property {import('types/api-responses').QuestionRadio} question
 * @property {string} labelId
 * @property {boolean} [isDisabled]
 * @property {(args: { option: QuestionOption, autoAdvance: boolean }) => void} onOptionChanged
 * @property {(submitState: number) => void} setSubmitState
 */

/**
 * @param {AgeNumpadQuestionProps} props
 * @returns {JSXElement}
 */
const AgeNumpadQuestion = ({ question, labelId, isDisabled = false, onOptionChanged, setSubmitState }) => {
  const initialAge = getInitialAgeFromOptions(question.options);

  const [firstDigit, setFirstDigit] = useState(
    /** @type {string | null} */ (initialAge !== null ? String(initialAge)[0] : null),
  );
  const [confirmedAge, setConfirmedAge] = useState(/** @type {number | null} */ (initialAge));

  useEffect(() => {
    if (initialAge !== null) {
      setSubmitState(SubmitState.Ready);
    }
  }, []);

  const handleBackspace = () => {
    if (confirmedAge !== null) {
      // Drop the second digit; keep firstDigit so the next tap completes the age.
      setConfirmedAge(null);
      setSubmitState(SubmitState.NotReady);
    } else {
      setFirstDigit(null);
    }
  };

  /** @param {string} digit */
  const handleDigitPress = (digit) => {
    if (firstDigit === null || confirmedAge !== null) {
      // Starting fresh (or overwriting a confirmed age) — this tap is the first digit.
      setFirstDigit(digit);
      setConfirmedAge(null);
      setSubmitState(SubmitState.NotReady);
      return;
    }
    const age = parseInt(firstDigit + digit, 10);
    setConfirmedAge(age);
    const option = findOptionForAge(age, question.options);
    if (option) {
      // autoAdvance:false -> user reviews and taps Next, matching the slider variant.
      onOptionChanged({ option, autoAdvance: false });
      setSubmitState(SubmitState.Ready);
    }
  };

  const validDigits =
    firstDigit === null || confirmedAge !== null ? VALID_FIRST_DIGITS : getValidSecondDigits(firstDigit);

  const displayFirst = confirmedAge !== null ? String(confirmedAge)[0] : firstDigit;
  const displaySecond = confirmedAge !== null ? String(confirmedAge)[1] : null;
  const hasInput = firstDigit !== null || confirmedAge !== null;

  return (
    <div className="csw-age-numpad-question" aria-labelledby={labelId}>
      <div className="csw-age-numpad-display" aria-live="polite">
        <span className={`csw-age-numpad-slot${displayFirst ? " csw-age-numpad-slot-filled" : ""}`}>
          {displayFirst}
        </span>
        <span className={`csw-age-numpad-slot${displaySecond ? " csw-age-numpad-slot-filled" : ""}`}>
          {displaySecond}
        </span>
      </div>

      <div className="csw-age-numpad-grid">
        {NUMPAD_KEYS.map((key, index) => {
          if (key === "backspace") {
            return (
              <button
                key="backspace"
                type="button"
                className="csw-btn csw-age-numpad-key csw-age-numpad-key-backspace outline"
                onClick={handleBackspace}
                disabled={isDisabled || !hasInput}
                aria-label="Backspace"
              >
                {"←"}
              </button>
            );
          }
          if (key === null) {
            return <span key={`empty-${index}`} aria-hidden="true" />;
          }
          // Out-of-range digits are truly disabled (not just visually muted) so keyboard
          // and assistive-tech users can't activate them into an uncommittable age.
          const isOutOfRange = !validDigits.has(key);
          return (
            <button
              key={key}
              type="button"
              className="csw-btn csw-age-numpad-key outline"
              onClick={() => handleDigitPress(key)}
              disabled={isDisabled || isOutOfRange}
              aria-label={key}
            >
              {key}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** @type {ComponentType<AgeNumpadQuestionProps>} */
export default connect("", actions)(AgeNumpadQuestion);
